import * as yup from "yup";
import {
  getTodayDateString,
  getCurrentTimeString,
  timeToMinutes,
} from "../utils/visitorHelpers";
import {
  validateIdProofNumber,
  validateIdProofPair,
  normalizeIdProofNumber,
  resolveIdProofKind,
} from "./idProofValidation";

const mobileSchema = yup
  .string()
  .trim()
  .required("Mobile number is required")
  .matches(/^[0-9]{10}$/, "Mobile must be exactly 10 digits");

const VISITOR_NAME_MAX_LEN = 30;
const VISITOR_NAME_MIN_LEN = 3;

export const VISITOR_NAME_NO_DIGITS_MSG =
  "Visitor name cannot contain numbers. Use letters only.";

/** Strip digits while typing so numbers never appear in the field. */
export function normalizeVisitorName(value) {
  return String(value ?? "")
    .replace(/\d/g, "")
    .slice(0, VISITOR_NAME_MAX_LEN);
}

const visitorNameSchema = yup
  .string()
  .trim()
  .required("Visitor name is required")
  .min(VISITOR_NAME_MIN_LEN, `Visitor name must be at least ${VISITOR_NAME_MIN_LEN} characters`)
  .max(VISITOR_NAME_MAX_LEN, `Visitor name must be at most ${VISITOR_NAME_MAX_LEN} characters`)
  .test("letters-only", VISITOR_NAME_NO_DIGITS_MSG, (value) => {
    if (!value) return true;
    return !/\d/.test(value) && /^[A-Za-z\s]+$/.test(value);
  });

function inTimeNotFuture(value) {
  if (!value) return true;
  const now = getCurrentTimeString();
  const inMin = timeToMinutes(value);
  const nowMin = timeToMinutes(now);
  if (inMin === null || nowMin === null) return true;
  return inMin <= nowMin;
}

/** In time vs clock only when entry date is today (edits on past dates allowed). */
function inTimeNotFutureWhenEntryToday(value, context) {
  const entryDate = context?.parent?.entry_date;
  const today = getTodayDateString();
  if (entryDate && String(entryDate) !== today) return true;
  return inTimeNotFuture(value);
}

function outTimeAfterInTime(outTime, context) {
  if (!outTime) return true;
  const inTime = context.parent?.in_time;
  const inMin = timeToMinutes(inTime);
  const outMin = timeToMinutes(outTime);
  if (inMin === null || outMin === null) return true;
  return outMin >= inMin;
}

function buildIdProofFields(getIdProofTypeName = () => null) {
  const idProofTypeField = yup
    .string()
    .nullable()
    .transform((v) => v ?? "")
    .test("id-proof-type-pair", function (value) {
      const pairError = validateIdProofPair({
        typeId: value,
        number: this.parent?.id_proof_number,
      });
      if (pairError?.field === "id_proof_type_id") {
        return this.createError({ message: pairError.message });
      }
      return true;
    });

  const idProofNumberField = yup
    .string()
    .nullable()
    .transform((v) => (v == null ? "" : v))
    .test("id-proof-number-pair", function (value) {
      const pairError = validateIdProofPair({
        typeId: this.parent?.id_proof_type_id,
        number: value,
      });
      if (pairError?.field === "id_proof_number") {
        return this.createError({ message: pairError.message });
      }
      return true;
    })
    .test("id-proof-number-format", function (value) {
      const typeId = this.parent?.id_proof_type_id;
      if (!typeId) return true;
      const typeName = getIdProofTypeName(typeId);
      const formatError = validateIdProofNumber(typeName, value);
      if (formatError) return this.createError({ message: formatError });
      return true;
    });

  return { idProofTypeField, idProofNumberField };
}

/** Full visitor form (create / admin+office edit). */
export function buildVisitorFullSchema({
  entryDateLocked = true,
  getIdProofTypeName,
} = {}) {
  const today = getTodayDateString();
  let entryDateField = yup.string().required("Entry date is required");
  if (entryDateLocked) {
    entryDateField = entryDateField.oneOf(
      [today],
      "Entry date must be today's date only",
    );
  }

  const { idProofTypeField, idProofNumberField } = buildIdProofFields(getIdProofTypeName);

  return yup.object({
    visitor_name: visitorNameSchema,
    mobile_number: mobileSchema,
    address: yup
      .string()
      .trim()
      .max(1000, "Address must be less than 1000 characters")
      .nullable()
      .transform((v) => (v == null ? "" : v)),
    purpose_id: yup
      .mixed()
      .required("Purpose of visit is required")
      .test("purpose", "Purpose of visit is required", (v) => v !== "" && v != null),
    person_to_meet: yup
      .mixed()
      .required("Select a staff member from the search results")
      .test(
        "person",
        "Select a staff member from the search results (click the name or press Enter)",
        (v) => v !== "" && v != null && v !== undefined,
      ),
    entry_date: entryDateField,
    in_time: yup
      .string()
      .required("In time is required")
      .test(
        "not-future",
        "In time cannot be a future time",
        inTimeNotFutureWhenEntryToday,
      ),
    out_time: yup
      .string()
      .nullable()
      .transform((v) => (v == null || v === "" ? "" : v))
      .test(
        "after-in",
        "Out time cannot be earlier than in time",
        outTimeAfterInTime,
      ),
    id_proof_type_id: idProofTypeField,
    id_proof_number: idProofNumberField,
    visitor_pass_issued: yup.boolean().default(false),
    approved_by: yup.string().nullable().transform((v) => v ?? ""),
    remarks: yup.string().nullable().transform((v) => v ?? ""),
  });
}

/** Receptionist / limited edit — directory columns + ID proof (same rules as register). */
export function buildVisitorDirectoryEditSchema({ getIdProofTypeName } = {}) {
  const { idProofTypeField, idProofNumberField } = buildIdProofFields(getIdProofTypeName);

  return yup.object({
    visitor_name: visitorNameSchema,
    mobile_number: mobileSchema,
    purpose_id: yup
      .mixed()
      .required("Purpose of visit is required")
      .test("purpose", "Purpose of visit is required", (v) => v !== "" && v != null),
    person_to_meet: yup
      .mixed()
      .required("Select a staff member from the search results")
      .test(
        "person",
        "Select a staff member from the search results (click the name or press Enter)",
        (v) => v !== "" && v != null && v !== undefined,
      ),
    entry_date: yup.string().required("Entry date is required"),
    in_time: yup
      .string()
      .required("In time is required")
      .test(
        "not-future",
        "In time cannot be a future time",
        inTimeNotFutureWhenEntryToday,
      ),
    out_time: yup
      .string()
      .nullable()
      .transform((v) => (v == null || v === "" ? "" : v))
      .test(
        "after-in",
        "Out time cannot be earlier than in time",
        outTimeAfterInTime,
      ),
    id_proof_type_id: idProofTypeField,
    id_proof_number: idProofNumberField,
  });
}

/** @deprecated Use buildVisitorDirectoryEditSchema */
export const visitorDirectoryEditSchema = buildVisitorDirectoryEditSchema();

/** @deprecated Use buildVisitorDirectoryEditSchema */
export const visitorLimitedSchema = visitorDirectoryEditSchema;

export function mapYupErrorsToRhf(yupError) {
  const out = {};
  if (!yupError?.inner?.length) {
    if (yupError?.path) out[yupError.path] = { message: yupError.message };
    return out;
  }
  for (const err of yupError.inner) {
    if (err.path && !out[err.path]) {
      out[err.path] = { message: err.message };
    }
  }
  return out;
}

export function buildVisitorPayload(values, { limited = false, idProofTypes = [] } = {}) {
  const idProofPayload = {};
  if (values.id_proof_type_id) {
    idProofPayload.id_proof_type_id = Number(values.id_proof_type_id);
    const typeRow = (idProofTypes || []).find(
      (t) => String(t.id) === String(values.id_proof_type_id),
    );
    const kind = resolveIdProofKind(typeRow?.type_name);
    idProofPayload.id_proof_number = normalizeIdProofNumber(
      kind,
      values.id_proof_number,
    );
  } else {
    idProofPayload.id_proof_type_id = null;
    idProofPayload.id_proof_number = "";
  }

  if (limited) {
    return {
      visitor_name: String(values.visitor_name ?? "").trim(),
      mobile_number: String(values.mobile_number ?? "").trim(),
      purpose_id: Number(values.purpose_id),
      person_to_meet: Number(values.person_to_meet),
      entry_date: values.entry_date,
      in_time: values.in_time,
      out_time: values.out_time || null,
      ...idProofPayload,
    };
  }

  const payload = {
    visitor_name: String(values.visitor_name ?? "").trim(),
    mobile_number: String(values.mobile_number ?? "").trim(),
    address: values.address ? String(values.address).trim() : "",
    purpose_id: Number(values.purpose_id),
    person_to_meet: Number(values.person_to_meet),
    entry_date: values.entry_date,
    in_time: values.in_time,
    out_time: values.out_time || null,
    id_proof_number: values.id_proof_number
      ? String(values.id_proof_number).trim()
      : "",
    visitor_pass_issued: values.visitor_pass_issued ? 1 : 0,
    remarks: values.remarks ? String(values.remarks).trim() : "",
  };

  if (values.id_proof_type_id) {
    payload.id_proof_type_id = Number(values.id_proof_type_id);
    const typeRow = (idProofTypes || []).find(
      (t) => String(t.id) === String(values.id_proof_type_id),
    );
    const kind = resolveIdProofKind(typeRow?.type_name);
    payload.id_proof_number = normalizeIdProofNumber(kind, values.id_proof_number);
  }

  if (values.approved_by) {
    payload.approved_by = Number(values.approved_by);
  }

  return payload;
}

export function getDefaultVisitorFormValues() {
  return {
    visitor_name: "",
    mobile_number: "",
    address: "",
    purpose_id: "",
    person_to_meet: "",
    entry_date: getTodayDateString(),
    in_time: getCurrentTimeString(),
    out_time: "",
    id_proof_type_id: "",
    id_proof_number: "",
    visitor_pass_issued: false,
    approved_by: "",
    remarks: "",
  };
}

export function visitorToFormValues(visitor) {
  if (!visitor) return getDefaultVisitorFormValues();
  const fmtTime = (t) => {
    if (!t) return "";
    const s = String(t);
    return s.length >= 5 ? s.slice(0, 5) : s;
  };
  const fmtDate = (d) => {
    if (!d) return getTodayDateString();
    const s = String(d);
    return s.includes("T") ? s.slice(0, 10) : s.slice(0, 10);
  };

  return {
    visitor_name: visitor.visitor_name ?? "",
    mobile_number: visitor.mobile_number ?? "",
    address: visitor.address ?? "",
    purpose_id: visitor.purpose_id != null ? String(visitor.purpose_id) : "",
    person_to_meet:
      visitor.person_to_meet != null ? String(visitor.person_to_meet) : "",
    entry_date: fmtDate(visitor.entry_date),
    in_time: fmtTime(visitor.in_time),
    out_time: fmtTime(visitor.out_time),
    id_proof_type_id:
      visitor.id_proof_type_id != null ? String(visitor.id_proof_type_id) : "",
    id_proof_number: visitor.id_proof_number ?? "",
    visitor_pass_issued: Boolean(
      visitor.visitor_pass_issued === 1 || visitor.visitor_pass_issued === true,
    ),
    approved_by:
      visitor.approved_by != null ? String(visitor.approved_by) : "",
    remarks: visitor.remarks ?? "",
  };
}
